import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  deleteClientSegment,
  getClientSegmentsFilteredLite,
  getClientSegmentsCount,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import { formatRecommendationMessage } from "../../helpers/RecommendationMessageHelper";
import { COMMERCIAL_INTELLIGENCE_PATHS } from "../../helpers/MenuPathHelper";

import {
  Table,
  Button,
  message,
  Modal,
  Input,
  Tag,
  InputNumber,
  Dropdown,
  Menu,
  Tooltip,
  Select,
  DatePicker,
  Skeleton,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import { RxGroup } from "react-icons/rx";
import PageHeader from "../../components/pageheader/PageHeader";
import EmptyState from "../../components/emptystate/EmptyState";

import styles from "./ClientSegments.module.scss";
import moment from "moment/moment";

const { RangePicker } = DatePicker;

const DEBOUNCE_MS = 400;

const OPPORTUNITY_TYPE_INT_OPTIONS = [1, 2, 3, 5, 6, 7, 8, 9] as const;

const getOpportunityFamily = (
  typeInt: number | null | undefined
): { labelKey: string; color: string } | null => {
  switch (typeInt) {
    case 1:
      return { labelKey: "SegmentFamily-EconomicReactivation", color: "magenta" };
    case 2:
      return { labelKey: "SegmentFamily-CategoryAffinity", color: "cyan" };
    case 3:
      return { labelKey: "SegmentFamily-AttributeBuyers", color: "purple" };
    case 5:
      return { labelKey: "SegmentFamily-BrandAffinity", color: "blue" };
    case 6:
      return { labelKey: "SegmentFamily-LowPerformance", color: "volcano" };
    case 7:
      return { labelKey: "SegmentFamily-StockOpportunity", color: "geekblue" };
    case 8:
      return { labelKey: "SegmentFamily-Loyalty", color: "green" };
    case 9:
      return { labelKey: "SegmentFamily-Financial", color: "gold" };
    default:
      return null;
  }
};

const ClientSegments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const fetchRequestIdRef = useRef(0);

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/segments";
    }
    if (location.pathname.startsWith("/fidelity")) {
      return "/fidelity/clientsegments";
    }
    return "/promotions/clientsegments";
  };

  const [data, setData] = useState<any[]>([]);
  const [rawSegments, setRawSegments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const params = new URLSearchParams(location.search);
  const [actualPage, setActualPage] = useState(Number(params.get("page")) || 1);
  const [nameFilterValue, setNameFilterValue] = useState(
    params.get("name") || "",
  );
  const [debouncedNameFilterValue, setDebouncedNameFilterValue] = useState(
    params.get("name") || "",
  );
  const [descriptionFilterValue, setDescriptionFilterValue] = useState(
    params.get("description") || "",
  );
  const [debouncedDescriptionFilterValue, setDebouncedDescriptionFilterValue] =
    useState(params.get("description") || "");
  const [minMembersFilterValue, setMinMembersFilterValue] = useState<
    number | null
  >(params.get("minMembers") ? Number(params.get("minMembers")) : null);
  const [maxMembersFilterValue, setMaxMembersFilterValue] = useState<
    number | null
  >(params.get("maxMembers") ? Number(params.get("maxMembers")) : null);
  const [originFilterValue, setOriginFilterValue] = useState<
    string | undefined
  >(params.get("origin") || "SUGGESTED");
  const [typeIntFilterValue, setTypeIntFilterValue] = useState<
    number | undefined
  >(
    params.get("typeInt") ? Number(params.get("typeInt")) : undefined,
  );
  const [dateRangeFilterValue, setDateRangeFilterValue] = useState<
    [moment.Moment, moment.Moment] | null
  >(
    params.get("startDate") && params.get("endDate")
      ? [moment(params.get("startDate")), moment(params.get("endDate"))]
      : null,
  );
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedNameFilterValue(nameFilterValue);
      setDebouncedDescriptionFilterValue(descriptionFilterValue);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [nameFilterValue, descriptionFilterValue]);

  const handleFetchClientSegments = useCallback(
    async (pagination = actualPage) => {
      const requestId = ++fetchRequestIdRef.current;
      setLoading(true);

      const hasQuantityFilters =
        minMembersFilterValue !== null || maxMembersFilterValue !== null;

      const response = await getClientSegmentsFilteredLite(pagination, {
        name: debouncedNameFilterValue,
        description: debouncedDescriptionFilterValue,
        minMembers: undefined,
        maxMembers: undefined,
        origin: originFilterValue,
        typeInt: typeIntFilterValue,
        startDate: dateRangeFilterValue
          ? dateRangeFilterValue[0]?.format("YYYY-MM-DD") + "T00:00:00"
          : undefined,
        endDate: dateRangeFilterValue
          ? dateRangeFilterValue[1]?.format("YYYY-MM-DD") + "T23:59:59"
          : undefined,
      });

      if (requestId !== fetchRequestIdRef.current) return;

      const segmentsNoCount = (response.clientSegments || []).map((s: any) => ({
        ...s,
        count: undefined,
        audienceMail: undefined,
        audienceCellular: undefined,
      }));

      setRawSegments(segmentsNoCount);
      setData(segmentsNoCount);
      const totalFromApi = hasQuantityFilters
        ? segmentsNoCount.length
        : response.totalElements;
      setTotalSize(
        Math.max(segmentsNoCount.length, totalFromApi ?? 0),
      );

      setLoading(false);

      const ids = segmentsNoCount.map((s: any) => s.id).filter(Boolean);
      if (ids.length > 0) {
        setLoadingCounts(true);
        const countsResponse: any = await getClientSegmentsCount(ids);
        const countsList = countsResponse?.data ?? [];
        const countsById = new Map<number, any>(
          (countsList || []).map((c: any) => [Number(c.id), c]),
        );

        if (requestId !== fetchRequestIdRef.current) return;

        const withCounts = segmentsNoCount.map((s: any) => {
          const metrics = countsById.get(Number(s.id));
          return {
            ...s,
            count: metrics ? Number(metrics.count ?? 0) : 0,
            audienceMail: metrics ? Number(metrics.audienceMail ?? 0) : 0,
            audienceCellular: metrics ? Number(metrics.audienceCellular ?? 0) : 0,
            averageTicketMin:
              metrics?.audienceTicketMin ?? s.averageTicketMin,
            averageTicketMax:
              metrics?.audienceTicketMax ?? s.averageTicketMax,
            totalSpent: metrics?.audienceTotalSpent ?? s.totalSpent,
          };
        });

        setRawSegments(withCounts);

        if (hasQuantityFilters) {
          let filtered = withCounts;
          if (minMembersFilterValue !== null) {
            filtered = filtered.filter(
              (segment: any) => segment.count >= minMembersFilterValue,
            );
          }
          if (maxMembersFilterValue !== null) {
            filtered = filtered.filter(
              (segment: any) => segment.count <= maxMembersFilterValue,
            );
          }
          setData(filtered);
          setTotalSize(filtered.length);
        } else {
          setData(withCounts);
        }
        setLoadingCounts(false);
      } else {
        setLoadingCounts(false);
      }
    },
    [
      actualPage,
      debouncedNameFilterValue,
      debouncedDescriptionFilterValue,
      minMembersFilterValue,
      maxMembersFilterValue,
      originFilterValue,
      typeIntFilterValue,
      dateRangeFilterValue,
    ],
  );

  const updateUrlWithFilters = useCallback(
    (filters: {
      name?: string;
      description?: string;
      minMembers?: number | null;
      maxMembers?: number | null;
      origin?: string;
      typeInt?: number | null;
      startDate?: moment.Moment | null;
      endDate?: moment.Moment | null;
      page?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (filters.name) searchParams.set("name", filters.name);
      if (filters.description)
        searchParams.set("description", filters.description);
      if (filters.minMembers !== null && filters.minMembers !== undefined)
        searchParams.set("minMembers", String(filters.minMembers));
      if (filters.maxMembers !== null && filters.maxMembers !== undefined)
        searchParams.set("maxMembers", String(filters.maxMembers));
      if (filters.origin) searchParams.set("origin", filters.origin);
      if (filters.typeInt !== null && filters.typeInt !== undefined)
        searchParams.set("typeInt", String(filters.typeInt));
      if (filters.startDate)
        searchParams.set("startDate", filters.startDate.format("YYYY-MM-DD"));
      if (filters.endDate)
        searchParams.set("endDate", filters.endDate.format("YYYY-MM-DD"));
      if (filters.page) searchParams.set("page", String(filters.page));
      navigate({ search: searchParams.toString() }, { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleFetchClientSegments();
    } else {
      setLoading(false);
    }
  }, [handleFetchClientSegments]);

  useEffect(() => {
    if (!hasPermission(privilegesEnum.READ_CLIENTS)) return;
    updateUrlWithFilters({
      name: debouncedNameFilterValue,
      description: debouncedDescriptionFilterValue,
      minMembers: minMembersFilterValue,
      maxMembers: maxMembersFilterValue,
      origin: originFilterValue,
      typeInt: typeIntFilterValue,
      startDate: dateRangeFilterValue ? dateRangeFilterValue[0] : null,
      endDate: dateRangeFilterValue ? dateRangeFilterValue[1] : null,
      page: actualPage,
    });
  }, [
    actualPage,
    debouncedNameFilterValue,
    debouncedDescriptionFilterValue,
    minMembersFilterValue,
    maxMembersFilterValue,
    originFilterValue,
    typeIntFilterValue,
    dateRangeFilterValue,
    updateUrlWithFilters,
  ]);

  const showDeleteConfirm = (clientSegmentId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDeleteSegment(clientSegmentId);
      },
    });
  };

  const handleDeleteSegment = async (clientSegmentId: number) => {
    setLoading(true);
    const deleteStatus = await deleteClientSegment(clientSegmentId);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchClientSegments();
    setLoading(false);
  };

  const handlePagination = (page: number) => {
    setActualPage(page);
    handleFetchClientSegments(page);
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setDescriptionFilterValue("");
    setDebouncedNameFilterValue("");
    setDebouncedDescriptionFilterValue("");
    setMinMembersFilterValue(null);
    setMaxMembersFilterValue(null);
    setOriginFilterValue(undefined);
    setTypeIntFilterValue(undefined);
    setDateRangeFilterValue(null);
    setActualPage(1);
    updateUrlWithFilters({
      name: "",
      description: "",
      minMembers: null,
      maxMembers: null,
      origin: undefined,
      typeInt: null,
      startDate: null,
      endDate: null,
      page: 1,
    });
  };

  const hasActiveFilters = () => {
    return (
      nameFilterValue !== "" ||
      descriptionFilterValue !== "" ||
      minMembersFilterValue !== null ||
      maxMembersFilterValue !== null ||
      originFilterValue !== undefined ||
      typeIntFilterValue !== undefined ||
      dateRangeFilterValue !== null
    );
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="ClientSegment" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="ClientSegment"
          createPath={`${getBasePath()}/create`}
          createPermission={privilegesEnum.WRITE_CLIENTS}
        />
      );
    }

    return null;
  };

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <Link
          to={`${getBasePath()}/${record.id}/view`}
          className={styles.nameLink}
        >
          {formatRecommendationMessage(
            t,
            record.nameKey,
            record.nameParams,
            name
          )}
        </Link>
      ),
    },
    {
      title: t("Common-Type"),
      key: "type",
      render: (segment: any) => segment.type.description_es,
    },
    {
      title: t("ClientSegment-OpportunityFamily"),
      key: "typeInt",
      render: (segment: any) => {
        const family = getOpportunityFamily(segment.typeInt);
        if (!family) return "";
        return <Tag color={family.color}>{t(family.labelKey)}</Tag>;
      },
    },
    {
      title: t("Common-Description"),
      dataIndex: "description",
      render: (description: string, record: any) =>
        formatRecommendationMessage(
          t,
          record.descriptionKey,
          record.descriptionParams,
          description
        ),
    },
    {
      title: t("Common-Quantity"),
      dataIndex: "count",
      render: (value: any) => {
        if (value === undefined || value === null) {
          return <Skeleton.Input active size="small" style={{ width: 60 }} />;
        }
        return value;
      },
    },
    {
      title: t("ClientSegment-TpMin"),
      dataIndex: "averageTicketMin",
      render: (value: number | null) =>
        value === null || value === undefined
          ? "-"
          : `$${Number(value).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`,
    },
    {
      title: t("ClientSegment-TpMax"),
      dataIndex: "averageTicketMax",
      render: (value: number | null) =>
        value === null || value === undefined
          ? "-"
          : `$${Number(value).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`,
    },
    {
      title: t("Common-TotalSpent"),
      dataIndex: "totalSpent",
      render: (value: number | null) =>
        value === null || value === undefined
          ? "-"
          : `$${Number(value).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`,
    },
    {
      title: t("Common-Email"),
      dataIndex: "audienceMail",
      render: (value: any) => {
        if (value === undefined || value === null) {
          return <Skeleton.Input active size="small" style={{ width: 60 }} />;
        }
        return value;
      },
    },
    {
      title: t("Common-Cellphone"),
      dataIndex: "audienceCellular",
      render: (value: any) => {
        if (value === undefined || value === null) {
          return <Skeleton.Input active size="small" style={{ width: 60 }} />;
        }
        return value;
      },
    },
    {
      title: t("Common-CreatedDate"),
      dataIndex: "createdAt",
      render: (date: any) =>
        date ? moment(date, "YYYY-MM-DD").format("DD-MM-YYYY") : "",
    },
    {
      title: t("Common-Origin"),
      dataIndex: "origin",
      render: (origin: any) => {
        if (!origin) return "";

        const getOriginColor = (originValue: string) => {
          switch (originValue) {
            case "MANUAL":
              return "blue";
            case "SUGGESTED":
              return "purple";
            default:
              return "default";
          }
        };

        const originValue = typeof origin === "string" ? origin : origin.value;
        const originDescription =
          typeof origin === "string"
            ? origin === "MANUAL"
              ? "Manual"
              : origin === "SUGGESTED"
                ? "Sugerido"
                : origin
            : origin.description_es;

        return (
          <Tag color={getOriginColor(originValue)}>{originDescription}</Tag>
        );
      },
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, segment: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(`${getBasePath()}/${segment.id}/view`)}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_CLIENTS) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`${getBasePath()}/${segment.id}/view`)
                  }
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              {hasPermission(privilegesEnum.DELETE_CLIENTS) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(segment.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}

              <Menu.Item
                key="createCampaign"
                icon={<NotificationOutlined />}
                onClick={() =>
                  navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.campaigns}/create`, {
                    state: { clientSegmentIds: [segment.id] },
                  })
                }
              >
                {t("Common-NewCampaign")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const buildUrlParams = () => {
    const params = new URLSearchParams();
    if (nameFilterValue) params.set("name", nameFilterValue);
    if (descriptionFilterValue)
      params.set("description", descriptionFilterValue);
    if (minMembersFilterValue !== null && minMembersFilterValue !== undefined)
      params.set("minMembers", String(minMembersFilterValue));
    if (maxMembersFilterValue !== null && maxMembersFilterValue !== undefined)
      params.set("maxMembers", String(maxMembersFilterValue));
    if (originFilterValue) params.set("origin", originFilterValue);
    if (typeIntFilterValue !== undefined)
      params.set("typeInt", String(typeIntFilterValue));
    if (dateRangeFilterValue) {
      params.set("startDate", dateRangeFilterValue[0].format("YYYY-MM-DD"));
      params.set("endDate", dateRangeFilterValue[1].format("YYYY-MM-DD"));
    }
    if (actualPage) params.set("page", String(actualPage));
    return params.toString();
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("Common-ClientSegments")}
        icon={<RxGroup />}
        onCreateClick={() => navigate(`${getBasePath()}/create`)}
        createPermission={privilegesEnum.WRITE_CLIENTS}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByName")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setNameFilterValue(e.target.value);
                    setActualPage(1);
                  }}
                  value={nameFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-Description")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setDescriptionFilterValue(e.target.value);
                    setActualPage(1);
                  }}
                  value={descriptionFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <InputNumber
                  min={0}
                  placeholder={t("Common-QuantityFrom")}
                  className={styles.filterInputNumber}
                  onChange={(value) => {
                    setMinMembersFilterValue(
                      typeof value === "number" ? value : null,
                    );
                    setActualPage(1);
                  }}
                  value={minMembersFilterValue ?? undefined}
                />
                <InputNumber
                  min={0}
                  placeholder={t("Common-QuantityTo")}
                  className={styles.filterInputNumber}
                  onChange={(value) => {
                    setMaxMembersFilterValue(
                      typeof value === "number" ? value : null,
                    );
                    setActualPage(1);
                  }}
                  value={maxMembersFilterValue ?? undefined}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={originFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-Origin")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) {
                      setOriginFilterValue(option.value);
                      setActualPage(1);
                    }
                  }}
                  onClear={() => {
                    setOriginFilterValue(undefined);
                    setActualPage(1);
                  }}
                >
                  <Select.Option value="MANUAL">Manual</Select.Option>
                  <Select.Option value="SUGGESTED">Sugerido</Select.Option>
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={typeIntFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("ClientSegment-OpportunityFamily")}
                  filterOption={(input: string, option: any) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value) => {
                    setTypeIntFilterValue(
                      typeof value === "number" ? value : undefined,
                    );
                    setActualPage(1);
                  }}
                >
                  {OPPORTUNITY_TYPE_INT_OPTIONS.map((typeInt) => {
                    const family = getOpportunityFamily(typeInt);
                    if (!family) return null;
                    return (
                      <Select.Option key={typeInt} value={typeInt}>
                        {t(family.labelKey)}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <RangePicker
                  className={styles.filterInput}
                  value={dateRangeFilterValue}
                  onChange={(dates) => {
                    setDateRangeFilterValue(
                      dates as [moment.Moment, moment.Moment] | null,
                    );
                    setActualPage(1);
                  }}
                  placeholder={[t("Common-StartDate"), t("Common-EndDate")]}
                />
              </div>
              {hasActiveFilters() && (
                <div className={styles.filterWrapper}>
                  <Tooltip title={t("Common-Clear")}>
                    <Button
                      onClick={handleClearFilters}
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                    >
                      {t("Common-Clear")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>

          <Table
            size="small"
            className={styles.table}
            rowKey={(record) => record.id}
            loading={loading}
            columns={columns}
            dataSource={data}
            pagination={{
              total: totalSize,
              showSizeChanger: false,
              onChange: handlePagination,
              current: actualPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            locale={{
              emptyText: getEmptyState(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientSegments;
